import React from "react";
import { useTranslation } from "react-i18next";
import { useActions } from "actions";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputBase,
  alpha,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { makeStyles } from "@mui/styles";
import FolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import clsx from "clsx";
import _ from "lodash";

const PartyTemplatesDialog = (
  {
    showOverwriteTemplateDialog,
    socket,
    partyId,
    appliedTemplate,
    templates,
    setSending,
    setUploadProgress,
    setShowUpload,
    setUploadDescription,
  },
  ref
) => {
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const classes = useStyles({ windowWidth });
  const { SendFullTemplate, GetTemplate, StartTemplate, ApplyTemplate } =
    useActions();
  const { t } = useTranslation();

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [path, setPath] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [selected, select] = React.useState();
  const [sendAll, setSendAll] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [visibleFolders, setVisibleFolders] = React.useState([]);
  const [visibleFiles, setVisibleFiles] = React.useState([]);

  React.useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
    },
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setOpen(false);
    submitTemplateDialog(selected);
  };

  const submitTemplateDialog = async (templateId) => {
    if (sendAll) {
      setSending(true);
      setShowUpload(true);
      setUploadDescription(t("party.importing"));
      await SendFullTemplate(partyId, templateId);
      socket.emit("reloadMessages", {
        partyId,
      });
      setSending(false);
      setShowUpload(false);
      setUploadDescription("");
      setSendAll(false);
    } else {
      if (appliedTemplate?.templateId === templateId)
        return showOverwriteTemplateDialog(templateId);
      await GetTemplate(templateId);
      StartTemplate(partyId, templateId);
      ApplyTemplate(templateId);
    }
  };

  const applySearch = (item) => {
    const searchVal = searchValue.trim().toLowerCase();
    if (!searchVal) return true;
    const terms = searchVal.split(" ");
    let result = true;
    for (const term of terms) {
      const searchTerm = new RegExp("^(.*)" + term + "(.*)$");
      result = result && searchTerm.test(item.title.toLowerCase());
    }
    return result;
  };

  React.useEffect(() => {
    getTemplatesInPath();
  }, [path]);

  const getTemplatesInPath = () => {
    if (!path || path.length === 0) {
      setVisibleFolders(templates.filter((m) => m.isFolder && !m.path));
      setVisibleFiles(templates.filter((m) => !m.isFolder && !m.path));
    } else {
      setVisibleFolders(
        templates.filter((m) => m.isFolder && m.path === path.join("/"))
      );
      setVisibleFiles(
        templates.filter((m) => !m.isFolder && m.path === path.join("/"))
      );
    }
  };

  const handleSetPath = (folderId) => {
    if (folderId === "") {
      setPath([]);
      return;
    }
    const folder = templates.find((m) => m.id === folderId);
    setPath([...(folder.path ? folder.path.split("/") : []), folder.id]);
  };

  const renderFolder = (folder) => {
    return (
      <div
        className={classes.folder}
        key={folder.id}
        onClick={() => handleSetPath(folder.id)}
      >
        <FolderIcon className={classes.folderIcon} />
        <div className={classes.folderName}>{folder.title}</div>
      </div>
    );
  };

  const renderTemplate = (template) => {
    return (
      <div
        className={clsx(classes.template, {
          [classes.selected]: template.id === selected,
        })}
        key={template.id}
        onClick={() => select(template.id)}
      >
        <Typography className={classes.templateName}>
          &nbsp;{template.title}
          {!!template.sharedBy && (
            <span style={{ opacity: 0.5 }}>{` - ${template.sharedBy}`}</span>
          )}
        </Typography>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialog }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t("party.applyTemplateDialogTitle")}</DialogTitle>
        <DialogContent>
          <div className={classes.search}>
            <div className={classes.searchIcon}>
              <SearchIcon />
            </div>
            <InputBase
              classes={{
                root: classes.searchField,
                input: classes.searchInput,
              }}
              inputProps={{ "aria-label": "search" }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          {!searchValue.trim() && (
            <div className={classes.breadcrumbs}>
              <Button
                size="small"
                classes={{
                  root: classes.pathButton,
                  label: classes.pathButtonLabel,
                }}
                onClick={() => handleSetPath("")}
              >
                {t("templates.homeTitle")}
              </Button>
              {path.length > 0 && (
                <ChevronRight
                  fontSize="small"
                  className={classes.pathSeparator}
                />
              )}
              {path.length > 2 && (
                <>
                  <Button
                    size="small"
                    classes={{
                      root: classes.pathButton,
                      label: classes.pathButtonLabel,
                    }}
                    disabled
                  >
                    ...
                  </Button>
                  <ChevronRight
                    fontSize="small"
                    className={classes.pathSeparator}
                  />
                </>
              )}
              {path.slice(-2).map((p, index) => (
                <React.Fragment key={p}>
                  <Button
                    size="small"
                    classes={{
                      root: classes.pathButton,
                      label: classes.pathButtonLabel,
                    }}
                    onClick={() => handleSetPath(p)}
                  >
                    {templates.find((m) => m.id === p).title}
                  </Button>
                  {index < path.slice(-2).length - 1 && (
                    <ChevronRight
                      fontSize="small"
                      className={classes.pathSeparator}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          <div className={classes.mediaArea}>
            {!searchValue.trim() &&
              _.sortBy(visibleFolders, ["title"]).map(renderFolder)}
            {!searchValue.trim() &&
              _.sortBy(visibleFiles, ["title"]).map(renderTemplate)}
            {!!searchValue.trim() &&
              _.sortBy(templates.filter(applySearch), ["title"]).map(
                renderTemplate
              )}
          </div>
        </DialogContent>

        <FormControlLabel
          className={classes.sendAllCheckbox}
          control={
            <Checkbox color="primary" onChange={() => setSendAll(!sendAll)} />
          }
          label={t("general.sendWholeTemplate")}
        />

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t("general.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={!selected}
          >
            {t("general.apply")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    margin: theme.spacing(1),
    maxHeight: `calc(100% - ${theme.spacing(2)})`,
  },
  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha("rgb(180, 180, 180)", 0.15),
    "&:hover": {
      backgroundColor: alpha("rgb(180, 180, 180)", 0.25),
    },
    width: "auto",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  searchIcon: {
    height: "100%",
    width: theme.spacing(5),
    pointerEvents: "none",
    display: "flex",
    justifyContent: "center",
  },
  searchField: {
    color: "inherit",
  },
  searchInput: {
    padding: theme.spacing(1, 1, 1, 0),
    width: "100%",
  },
  template: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 40,
    width: 400,
    maxWidth: (props) => props.windowWidth - theme.spacing(4),
    backgroundColor: "#f8f8f8",
    borderRadius: 5,
    marginBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f2f2f2",
    },
  },
  selected: {
    backgroundColor: "#ddd",
    "&:hover": {
      backgroundColor: "#ddd",
    },
  },
  mediaArea: {
    maxHeight: "calc(100vh - 300px)",
    overflowY: "auto",
    width: 400,
    maxWidth: (props) => props.windowWidth - theme.spacing(4),
    marginLeft: -theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      marginLeft: 0,
    },
  },
  templateName: {
    flex: 1,
    whiteSpace: "pre",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sendAllCheckbox: {
    marginLeft: 10,
  },
  folderIcon: {
    color: "#666",
  },
  thumbnail: {
    width: 60,
    height: 60,
    backgroundSize: "cover",
    borderRadius: 3,
    marginRight: 5,
  },
  folderName: {
    flex: 1,
    fontSize: 17,
    textJustify: "baseline",
    paddingLeft: 5,
    marginBottom: -3,
    color: "#444",
    whiteSpace: "pre",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  breadcrumbs: {
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderColor: "rgba(160, 160, 160, 0.6)",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  folder: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 40,
    width: 400,
    maxWidth: (props) => props.windowWidth - theme.spacing(4),
    backgroundColor: "#f8f8f8",
    borderRadius: 5,
    marginBottom: 5,
    paddingLeft: 10,
    paddingRight: 10,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f2f2f2",
    },
  },
  pathButtonLabel: {
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  pathSeparator: {
    color: "#999",
    marginTop: 5,
    marginLeft: -5,
    marginRight: -5,
  },
  pathButton: {
    textTransform: "none",
    fontWeight: "normal",
    fontSize: "0.9rem",
    color: "#333",
    paddingBottom: 3,
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: 30,
    "& span": {
      pointerEvents: "none",
    },
    justifyContent: "flex-start",
  },
}));

export default React.forwardRef(PartyTemplatesDialog);
